import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Target, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const STATUS_STYLES = {
  ok: { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500', icon: CheckCircle2, label: 'On Track' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500', icon: AlertTriangle, label: 'Warning' },
  over: { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500', icon: AlertTriangle, label: 'Over Budget' },
};

const Budgets = () => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ category: '', monthly_limit: '' });
  const canEdit = user?.role !== 'viewer';

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const res = await axios.get(`${API}/budgets/status`);
      setStatuses(res.data);
    } catch (e) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const limit = parseFloat(formData.monthly_limit);
    if (!(limit > 0)) {
      toast.error('Monthly limit must be greater than 0');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/budgets/${editingId}`, { monthly_limit: limit });
        toast.success('Budget updated');
      } else {
        if (!formData.category.trim()) {
          toast.error('Category is required');
          return;
        }
        await axios.post(`${API}/budgets`, {
          category: formData.category.trim(),
          monthly_limit: limit,
        });
        toast.success('Budget created');
      }
      setDialogOpen(false);
      resetForm();
      fetchStatuses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save budget');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await axios.delete(`${API}/budgets/${id}`);
      toast.success('Budget deleted');
      fetchStatuses();
    } catch {
      toast.error('Failed to delete budget');
    }
  };

  const handleEdit = (b) => {
    setEditingId(b.id);
    setFormData({ category: b.category, monthly_limit: String(b.monthly_limit) });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ category: '', monthly_limit: '' });
  };

  return (
    <div className="p-8 md:p-12" data-testid="budgets-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="budgets-title">
              Budgets
            </h1>
            <p className="text-slate-600 mt-1 font-ibm">Set monthly spending caps per category and stay on track</p>
          </div>
          {canEdit && (
            <Dialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="add-budget-button"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  <Plus size={18} className="mr-2" />
                  Add Budget
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-chivo text-2xl">
                    {editingId ? 'Edit Budget' : 'New Budget'}
                  </DialogTitle>
                  <DialogDescription className="text-slate-600 font-ibm text-sm">
                    {editingId ? 'Update the monthly limit for this category.' : 'Set a monthly spending cap for a category.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="budget-form">
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                      Category
                    </Label>
                    <Input
                      data-testid="input-budget-category"
                      required
                      disabled={!!editingId}
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      placeholder="e.g., Marketing, Rent, Travel"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                      Monthly Limit
                    </Label>
                    <Input
                      data-testid="input-budget-limit"
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      value={formData.monthly_limit}
                      onChange={(e) => setFormData({ ...formData, monthly_limit: e.target.value })}
                      className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      placeholder="5000.00"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      data-testid="submit-budget"
                      className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                    >
                      {editingId ? 'Update' : 'Create'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-sm hover:scale-[0.98] transition-transform"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="budgets-loading">
            Loading budgets...
          </div>
        ) : statuses.length === 0 ? (
          <div
            className="bg-white border border-slate-200 rounded-sm p-12 text-center"
            data-testid="no-budgets"
          >
            <Target className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-xl font-chivo font-bold text-slate-900 mb-2">No budgets yet</h3>
            <p className="text-slate-600 font-ibm text-sm">
              {canEdit
                ? 'Set a monthly spending cap for any category to get alerts when you approach the limit.'
                : 'No budgets have been configured yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="budgets-grid">
            {statuses.map((b, idx) => {
              const s = STATUS_STYLES[b.status];
              const Icon = s.icon;
              const pct = Math.min(b.percent_used, 100);
              return (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white border border-slate-200 p-6 rounded-sm hover:border-slate-400 transition-colors"
                  data-testid={`budget-card-${b.category}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">
                        {b.month}
                      </p>
                      <h3 className="text-xl font-chivo font-bold text-slate-900 mt-1" data-testid={`budget-category-${b.category}`}>
                        {b.category}
                      </h3>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-sm ${s.bg}`} data-testid={`budget-status-${b.category}`}>
                      <Icon size={14} className={s.text} />
                      <span className={`text-xs font-ibm font-semibold uppercase tracking-widest ${s.text}`}>
                        {s.label}
                      </span>
                    </div>
                  </div>

                  <div className="mb-2 flex justify-between items-baseline">
                    <span className="text-2xl font-chivo font-bold text-slate-900 tabular-nums" data-testid={`budget-spent-${b.category}`}>
                      ${b.spent.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-sm font-ibm text-slate-600 tabular-nums">
                      / ${b.monthly_limit.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="w-full bg-slate-100 h-2 rounded-sm overflow-hidden mb-2">
                    <div
                      className={`h-full ${s.bar} transition-all`}
                      style={{ width: `${pct}%` }}
                      data-testid={`budget-bar-${b.category}`}
                    />
                  </div>

                  <div className="flex justify-between text-xs font-ibm text-slate-600 tabular-nums">
                    <span data-testid={`budget-percent-${b.category}`}>{b.percent_used.toFixed(1)}% used</span>
                    <span data-testid={`budget-remaining-${b.category}`}>
                      ${b.remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })} remaining
                    </span>
                  </div>

                  {canEdit && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                      <button
                        onClick={() => handleEdit(b)}
                        data-testid={`edit-budget-${b.category}`}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 hover:bg-slate-100 rounded-sm transition-colors text-slate-600"
                      >
                        <Edit size={14} />
                        <span className="text-xs font-ibm font-medium">Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(b.id)}
                        data-testid={`delete-budget-${b.category}`}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 hover:bg-red-50 rounded-sm transition-colors text-red-600"
                      >
                        <Trash2 size={14} />
                        <span className="text-xs font-ibm font-medium">Delete</span>
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Budgets;
